/* ==========================================================================
   TYMLUS CART UPSELL -- STAGE 1 (architecture / behaviour only)
   ==========================================================================
   Scope note: this file implements the recommendation engine and the
   AVAILABLE -> ADDING -> COMPLETED state machine only. The markup this
   script renders into #CartUpsell is a deliberately plain, unstyled TEST
   CONTROL -- not the final luxury UI shown in the Photoshop mock-ups
   (large image gallery, styled size selector, message card, carousel,
   progress indicator). That visual layer is Stage 2+.

   Architecture summary (see the Stage 1 completion report for the full
   writeup, including exactly which existing theme mechanisms this reuses
   and which behaviours were verified empirically against the live cart
   rather than assumed):

   - Recency/intent signal: cart.items[0].product_id. Verified empirically
     against this store's live cart (not assumed): Shopify prepends a
     genuinely NEW distinct line item to the FRONT of cart.items in this
     theme, and neither a /cart/change.js quantity edit nor a /cart/add.js
     re-add of an already-present variant ever reorders the array. index 0
     is therefore a reliable "most recently newly-added product" signal
     with no custom explicit-tracking mechanism required.
   - Reactivity: hooks the theme's OWN existing cart events instead of
     inventing a parallel system -- `document` events `drawerOpen.CartDrawer`
     (fires when the cart drawer opens; carries no payload, so this path
     reads the cart via GET /cart.js) and `cart:updated` (fires after every
     confirmed cart mutation made through the theme's own AJAX cart --
     add/change/remove all funnel through it; carries the fresh cart JSON
     as event.detail.cart, so no extra fetch is needed on this path).
   - Mount point: a single persistent <div id="CartUpsell"> rendered once
     by snippets/cart-upsell.liquid, sitting inside #CartDrawer's
     .drawer__scrollable, immediately after the [data-products] node.
     Verified empirically: this node, [data-products] and .drawer__footer
     are never replaced/re-created by the theme's own cart-update DOM
     patching (only their contents mutate), so this module's own listeners
     are attached exactly once for the life of the page and never risk
     duplicating across a drawer re-render.
   - Post-add drawer refresh: after this module's OWN successful add, the
     pre-existing (do-not-touch) drawer's line items and subtotal are kept
     correct via the theme's own real GET /cart?view=ajax endpoint plus
     the theme's own real, already-loaded money-format configuration
     (window.theme.settings.moneyFormat) -- never a guessed/hardcoded
     format. See the comment above refreshExistingDrawerDisplay() for the
     full empirical verification this was based on.
   - Completion persistence: a Shopify CART ATTRIBUTE
     (`_tymlus_upsell_completed`), written via the standard AJAX Cart API
     (POST /cart/update.js), not localStorage. It travels with the cart
     object itself, so it survives drawer close/reopen, page refresh and
     navigation, and a brand new cart naturally starts without it.
   - Merchandising data: see the Stage 1.9 addendum below -- this is no
     longer the Stage 1 TEST_LADDER table; it now reads the production
     merchandising graph from Shopify metafields/metaobjects.

   Stage 1.5 additions (production hardening, no rewrite):
   - State machine gained one in-memory-only state, CONVERTED_PENDING_
     PERSISTENCE, entered the instant this module's own /cart/add.js
     succeeds -- suppressing the module for the rest of this document
     immediately, independent of whether the subsequent completion-
     attribute persistence call succeeds, fails, or throws. See the state
     machine comment block and handleAddAttempt() below for the full
     reasoning. The durable, cross-page-load COMPLETED state (the real
     cart attribute) is unchanged.
   - tymlus_upsell_size_selected analytics event added, firing on a
     genuine shopper size/variant selection (see renderCandidate()).
   - Recency signal (cart.items[0].product_id) re-verified empirically
     against every real Tymlus cart-mutation path available in this
     theme; see the Stage 1.5 completion report for the full test matrix
     and result. No architecture change was needed.

   Stage 1.9 additions (Production Merchandising Graph V1, FINAL LOCKED
   SPECIFICATION implementation -- Theme 8.5 only; MAIN untouched):
   - The Stage 1 TEST_PRODUCTS/TEST_LADDER hardcoded tables are removed.
     The relationship graph is now sourced entirely from Shopify data, per
     the locked spec's Section 10: Class A from the existing
     `custom.cart_upsell_priority` list.product_reference metafield (one
     entry per product, on each of the 10 Class-A products -- no change to
     this metafield's shape), and Class B from the new
     `tymlus_upsell_compound_rule` metaobject (2 entries). Neither this
     file nor snippets/cart-upsell.liquid hardcodes a single product ID or
     relationship any more -- see readGraph()/GRAPH below and the
     <script id="TymlusUpsellGraph"> tag that snippet now renders. Class C
     remains reserved and empty (spec Section 2.3) -- no candidates of
     that class are ever produced.
   - computeSourceProductId() (single-source: cart.items[0] only) is
     replaced by buildCandidates()/recencyIndexOf(), which evaluate the
     relationship graph against EVERY product currently in the cart, per
     the locked spec's Tier -> Recency -> Strength precedence (Sections 3,
     3.1, 4, 6): tier decides first; within a tier, the qualifying
     relationship whose source product has the LOWEST cart.items index
     (most recently added) wins; attach rate is only a tertiary tie-break
     for genuinely indistinguishable recency. The recency signal itself
     (cart.items array position) is unchanged from Stage 1/1.5 -- only its
     scope of application widens from "the one most recent product" to
     "every product in the cart, ranked".
   - The single-source resolveCandidate()'s ordered try/fallback walk is
     preserved unchanged in shape: it still tries candidates one at a time
     via /products/<handle>.js and stops at the first that survives
     availability/exclusion/gift-card filtering (spec Section 5) -- it now
     walks a graph-derived, full precedence-ordered candidate list instead
     of one source's curated ladder. This is also the entire mechanism
     behind the spec's Section 7 structural fallback: a filtered-out top
     candidate simply falls through to the next entry in the same
     precedence-ordered list, which may belong to a different tier -- no
     separate "retry" code path exists or is needed.
   - State machine, journey-completion semantics (only a THROUGH-module add
     completes the journey; every manual addition, including of the exact
     recommended product via its own PDP, only triggers recalculation),
     completion persistence, analytics event names/payloads, drawer-
     refresh mechanics, and availability re-evaluation cadence (natural
     evaluation points only, no polling) are ALL unchanged from Stage
     1/1.5. Nothing about them was touched.

   Stage 2D addition -- media carousel progress indicator only
   (wireMediaCarousel() below, plus the reset it needs in resetCardEntry()).
   [SUPERSEDED by Stage 2L -- see below. At the time this note described a
   pure CSS scroll-snap track; that track no longer exists.]

   Stage 2G/2H/2I/2J (superseded by Stage 2L, summarized here for the
   record rather than left describing code that no longer exists): these
   four stages built and then repeatedly patched a BESPOKE infinite-loop
   carousel on top of native browser scrolling -- a real `overflow-x: auto`
   track with CSS scroll-snap, two synthesized DOM clone slides at the
   boundaries, and a JS `settleTo()` that watched the track's own
   `scrollLeft` (via `scroll`/`scrollend` events, a settle-debounce, and
   two nested requestAnimationFrame calls) to detect a landing on a clone
   and silently re-point `scrollLeft` at the equivalent real slide. Each
   stage fixed one more real-iPhone-Safari symptom of that approach (a
   second visible jump, a blank clone frame, a motion hitch from scroll-
   snap re-evaluating the corrective jump) without ever fully closing the
   gap between a boundary loop transition and an ordinary adjacent swipe,
   because every fix was a reaction to the native scroll/snap/momentum
   subsystem's own behaviour, not a replacement of it.

   Stage 2L -- PDP carousel architecture audit + reuse (full audit/decision
   record kept in this stage's completion report; summarized here as the
   in-code rationale). Before patching the loop boundary a fifth time, this
   stage traced the ACTUAL deployed code (not class names) behind the
   standard product-page image gallery (snippets/product-images.liquid +
   theme.js's theme.Product.prototype.initProductSlider) and found it is
   not a bespoke system at all: it is the real Flickity library, wrapped by
   this theme's own already-global, already-loaded, purpose-built
   theme.Slideshow class (assets/theme.js). Flickity never hands swipe
   physics to the browser's native scroll/scroll-snap/momentum machinery in
   the first place -- it runs its own self-contained drag-physics
   simulation on a `transform`-positioned track, with its own native
   `wrapAround: true` infinite-loop option (real internal clone cells,
   fully managed by the library, never exposed to this file as DOM nodes to
   track or correct). That is architecturally why the PDP gallery has no
   equivalent loop-boundary glitch to chase: the whole class of iOS Safari
   WebKit quirks Stage 2G-2J kept fighting (elastic-bounce past a scroll
   boundary, scroll-snap re-evaluating a JS-set scrollLeft) can only happen
   to a NATIVE scroll container, and Flickity's mechanism is never one.

   wireMediaCarousel() below now instantiates the SAME theme.Slideshow
   class the PDP gallery uses (`new window.theme.Slideshow(mediaTrack,
   {...})`), not a re-implementation of it -- direct reuse, not a rewrite.
   Only the configuration differs from PDP's, and only where this card's
   own locked requirements genuinely differ from a product gallery's:
   `adaptiveHeight: false` (PDP's gallery resizes its box per slide; this
   card's overlaid size-selector/CTA require the fixed aspect-ratio box
   Stage 2D/2I established to stay put) and `setGallerySize: false` (the
   fixed-aspect-ratio box owns this card's height via CSS, not Flickity's
   own per-cell measurement -- the theme's own sitewide `.flickity-
   viewport{height:100%}`/`.flickity-slider{height:100%}` base rules,
   confirmed already present in theme.css, make that box-owns-height
   arrangement work with zero new CSS of this file's own). No PDP-specific
   glue (thumbnail-strip childNav sync, the 25px dragThreshold override,
   avoidReflow) is reused, since none of it applies to a card with no
   thumbnail strip. `makeCloneSlide()`, `settleTo()`, `currentRawIndex()`,
   `handleSettled()`, `settleAfterPaint()`, the manual clone insertion, and
   every scroll/scrollend listener are deleted outright -- Flickity's own
   `change` event (wired through theme.Slideshow's existing, already-proven
   `args.callbacks.onChange` extension point -- the exact same mechanism
   theme.Product already uses for the PDP gallery, traced directly from its
   own initProductSlider()) reports the real, already-wrapped slide index
   directly, with no clone-boundary math needed at this call site at all.
   The one piece of genuinely new code is the explicit `resize()` +
   instant `select(0, false, true)` pair in renderCandidate() (Flickity's
   own real select() signature, confirmed directly in vendor-scripts-v11.js)
   -- needed because, unlike a PDP gallery (always visible when its slider
   is constructed), every candidate card here is wired up front while
   still `hidden`, so its Flickity instance is built against a zero-width
   element and must be told to remeasure once the card is actually shown.
   The progress indicator keeps its own existing DOM/CSS (.tymlus-upsell__
   progress / -fill, unchanged by this stage except the Objective 5 gap
   value) rather than switching to Flickity's native pageDots feature,
   specifically because this card's progress track is a plain sibling
   BELOW the media box (Stage 2D/2E.2's locked, deliberately-not-PDP-
   matching placement) and Flickity's own dots would render inside the
   clipped Flickity container instead. Horizontal swipe is now Flickity's
   own drag handling rather than a scroll container's native touch
   scrolling; vertical drawer scroll remains untouched either way, since
   Flickity only ever captures a horizontal drag gesture (the same global,
   sitewide touch-scroll-conflict mitigation this theme already ships for
   every other Flickity instance on the site applies here too, automatically,
   with no new code in this file).

   Stage 2M -- carousel runtime-lifecycle audit (real-device report: the
   carousel eventually freezes on one image after prolonged interaction).
   Proven live against the deployed Theme 8.5 carousel, not inferred: an
   animated Flickity transition (a real swipe release, or next()/
   previous()/an animated select()) runs one synchronous physics step and
   then depends entirely on requestAnimationFrame for every further step,
   including the step that clears Flickity's own isAnimating flag. If that
   animation begins while the page is not visible (a backgrounded/
   suspended tab, a tab switch mid-swipe, a bfcache-frozen page) and no
   further rAF callback ever arrives, the rendered position freezes
   permanently at whatever fraction it first reached, selectedIndex (and
   this file's own progress-fill) keep advancing normally on every further
   swipe with no visible effect, and isAnimating's own re-entry guard
   silently no-ops every later swipe attempt too -- so it never self-heals
   on its own. Full mechanism, evidence and the fix (flkResync(), wired to
   drawerOpen.CartDrawer/visibilitychange/pageshow) are documented in
   place, directly above where flkResync() is defined, near the bottom of
   this file. Nothing about wireMediaCarousel(), the Flickity
   configuration, the progress-indicator mechanism, or any candidate-
   resolution/merchandising code changes in this stage -- this is a
   self-recovery addition only.

   A second, independently reported defect this stage investigated --
   product-page "model is..." sizing text rendering unusually close to
   the size buttons after prolonged cart interaction -- was NOT
   reproduced. A full static audit found no cart-upsell-owned CSS
   selector, custom property, or JS DOM query anywhere in this file or
   snippets/cart-upsell.liquid that is unscoped from #CartUpsell/
   #CartDrawer (confirmed: every rule in that snippet's stylesheet is
   namespaced under .tymlus-upsell__*, and this file's few document-level
   queries all target specific, unrelated IDs). A live, multi-cycle stress
   pass against the deployed carousel -- repeated drawer open/close,
   repeated swiping, repeated size selection, a real quantity change and
   line removal via the theme's own controls, candidate replacement,
   cross-product navigation, and a genuine back/forward (bfcache-category)
   navigation -- measured the live gap between the size buttons and that
   text via getBoundingClientRect() before and after every step and never
   observed it change. The one related mechanism this stage did confirm
   live is that the PDP's own product gallery (assets/theme.js's
   theme.Slideshow, the exact same Flickity engine and the exact same
   requestAnimationFrame dependency) is susceptible to the identical
   interrupted-animation freeze as Stage 2M's carousel fix above -- but
   that file is a sitewide, shared component far outside this file's own
   scope, is not itself touched by this stage, and in the specific
   product tested its own adaptiveHeight box height did not change across
   an interrupted transition, so no causal link to the reported text-
   position shift was established. See this stage's completion report for
   the full evidence and the explicit recommendation that follows from
   it.

   Stage 2P -- info-banner singular/plural micro-copy. The banner sentence
   ("Many of our clients also purchased this piece with the item/items in
   your bag.") is now driven by cart.items.length (distinct cart LINES,
   never summed quantity -- a single line at any quantity stays singular;
   a second distinct line flips it to plural), applied via updateBannerText(),
   defined and fully documented immediately above recalculate() below.
   Called unconditionally as recalculate()'s first statement -- before
   STATE_ADDING's own early return (see the Stage 4/V2 header addendum
   for why COMPLETED/CONVERTED_PENDING no longer exist as separate early-
   return branches) -- so it tracks the cart on every existing
   recalculation path (drawerOpen.
   CartDrawer's fresh GET /cart.js, and cart:updated's e.detail.cart) with
   zero new event listeners and zero new network requests. Plain
   .textContent writes to the existing, pre-rendered .tymlus-upsell__
   banner-text nodes only -- no styling, dimensions, icon, spacing,
   border-radius, placement, or animation changed; no other candidate-
   resolution, completion, carousel, size-selector, CTA, subtotal, or
   cart-line behaviour touched.

   Stage 2Q -- banner-text fix for this module's OWN add path. Live QA
   surfaced that a successful upsell-driven add (handleAddAttempt() below)
   left the banner text stale: that path calls /cart/add.js directly via
   fetchJSON, never through the theme's own AJAX cart, so it never dispatches
   'cart:updated' and never touches drawerOpen.CartDrawer either -- the two
   paths Stage 2P's updateBannerText() call relies on. handleAddAttempt()
   now calls updateBannerText() itself, immediately on a successful add,
   using cartLineCountBefore + 1 (exact, not estimated -- every upsell
   target is guaranteed by buildCandidates() to be a genuinely new distinct
   line, and cartLineCountBefore is read off the same /cart.js response this
   function already fetches for cartValueBefore) -- so still zero additional
   network requests and zero new listeners. See the comment at that call
   site for the full reasoning.

   Stage 3.1 -- ordered multi-target fallback (buildCandidates() only).
   snippets/cart-upsell.liquid's classA edges now optionally carry a
   "priority" field (zero-based, from the cart_upsell_priority metafield
   list's own item order -- see that file's Stage 3.1 header note) for
   sources configured with more than one ordered target. buildCandidates()
   below now reads that field into each Class A candidate and uses it as
   the sort tie-break INSTEAD of ATTACH_RATE/strength, but only ever
   between candidates sharing the same source -- Tier and Recency
   precedence between different sources are completely unchanged. See the
   comments at the push site and the sort comparator below for the full
   reasoning. resolveCandidate()'s own fall-through walk needed zero
   changes: it already tries the sorted list in order and moves to the
   next entry on any ineligibility, which is exactly the "target 1 ->
   target 2 -> target 3" behaviour this addition unlocks.

   Stage 3.2 -- ATTACH_RATE and the `strength` field removed entirely
   (buildCandidates() only), after live testing confirmed removing them
   changes no reachable outcome (see the removal note above ATTACH_RATE's
   old location, near buildCandidates()). The precedence engine is now
   exactly Tier -> Recency -> Priority, with no dead/unused fields.

   Stage 3.3 -- real-device defect fix: handleAddAttempt() froze the size
   fieldset during the add-to-cart request via the native `.disabled`
   property, which the HTML spec cascades to every contained radio
   <input>. This card's stylesheet keys its genuine-sold-out treatment
   off `input:disabled`, so that cascade made every size -- including
   ones genuinely in stock -- flash the crossed-out sold-out look for the
   whole add-to-cart network round trip. Proven via frame-by-frame
   analysis of a real-iPhone recording (the flash exactly bracketed
   handleAddAttempt()'s ADDING window) and by static trace (no other
   function runs during that window; `state === STATE_ADDING` explicitly
   blocks recalculate()/renderCandidate() from touching this card, and
   the real per-variant availability array is untouched throughout). Only
   handleAddAttempt() (the freeze/unfreeze call sites), resetCardEntry()
   and renderCandidate() (their own unfreeze-on-reset call sites) change:
   `.disabled = true/false` on the fieldset is replaced with an `is-busy`
   class toggle, and cart-upsell.liquid gains one new CSS rule
   (`.tymlus-upsell__sizes.is-busy { pointer-events: none; }`) with no
   color/border/content declarations, so an in-flight add now blocks the
   exact same click-driven interaction as before with zero visible
   change to any size button, selected or not. Nothing about candidate
   resolution, the precedence engine, fallback, or genuine sold-out
   styling (still driven entirely by real variant.available data in
   renderCandidate(), completely untouched) changes here.
   ==========================================================================

   STAGE 4 / V2 -- RELATIONSHIP-SCOPED ELIGIBILITY LIFECYCLE.

   Supersedes the completion-suppression HALF of Stages 1/1.5 (the
   `_tymlus_upsell_completed` cart attribute and its `reconcileState()`
   read path) -- NOT the resolver, NOT already-in-cart filtering, NOT the
   Tier/Recency/Priority engine, NOT the ADDING freeze/is-busy protection,
   NOT the carousel/size/analytics-payload machinery, none of which change
   in this stage.

   The Stage 1/1.5 design (confirmed live and in source by a dedicated
   diagnostic, "TYMLUS -- Cart Upsell Suppression / Returning-Customer
   Eligibility Diagnostic") had exactly one flag, `_tymlus_upsell_completed
   = "true"`, checked once in `recalculate()` BEFORE candidate resolution
   ever ran. Accepting ANY one upsell therefore silently disabled the
   WHOLE module, for every unrelated relationship, for the rest of that
   cart's life -- the flag was never programmatically cleared by any code
   path. That is the defect this stage fixes.

   New model: a small, self-pruning ledger of individually-timestamped
   source->target relationships, still stored in the SAME single cart
   attribute (`_tymlus_upsell_completed` -- no new attribute was
   introduced; the value SHAPE changed, the key did not), each entry
   valid for a fixed COOLDOWN_MS (3 hours) from the moment that exact
   relationship was completed. A relationship in cooldown is excluded as
   ONE candidate among many inside `buildCandidates()` -- exactly the same
   architectural position as the pre-existing already-in-cart filter it
   now sits beside -- rather than aborting the whole module before
   `buildCandidates()`/`resolveCandidate()` ever run. A different,
   unrelated relationship is therefore never affected by another
   relationship's cooldown, and the Tier -> Recency -> Priority precedence
   among whatever remains eligible is completely unchanged.

   Ledger value shape (chosen after inspecting the identifiers already
   authoritative throughout this file -- `candidate.sourceId`/
   `candidate.targetId`, both plain numeric-Shopify-product-id strings):
   a JSON-encoded array of compact 3-element arrays,
   `[[sourceId, targetId, completedAtEpochMs], ...]`, e.g.
   `[["15637622489470","15637639070078",1788440000000]]`. Chosen over a
   verbose object-per-record shape because it is materially smaller (this
   is cart-attribute storage) while remaining exactly as parseable and
   exactly as inspectable in Shopify admin/API tooling as the previous
   plain `"true"` string was. IDs are stored as strings (matching how
   this file already treats every product id everywhere else, e.g.
   `cartProductIds`); timestamps are absolute `Date.now()` epoch
   milliseconds, never page-relative or timezone-dependent, so the
   cooldown comparison is stable across reload/navigation/a different
   browser timezone entirely.

   Legacy migration: a cart attribute still holding the literal Stage 1
   string `"true"` is handled by `parseLedger()` below purely as a matter
   of course, not a special case -- `JSON.parse('true')` yields the
   boolean `true`, which is not an Array, so it is treated exactly like
   any other non-array/malformed value: zero active cooldowns, fail open.
   No shopper needs a new cart, and nothing proactively rewrites an old
   cart's attribute merely because it was read -- the legacy value is
   naturally replaced the next time (if ever) that specific cart
   completes a new upsell, which writes the new JSON shape as a normal
   side effect of `handleAddAttempt()`'s existing persistence call.

   Multiple simultaneous cooldowns: storing only the single most recent
   completion would let a second, unrelated completion's write silently
   erase the first relationship's still-active cooldown (a real regression
   risk this stage was explicitly built to avoid) -- so the ledger is a
   list, pruned (expired records dropped, duplicate source->target entries
   collapsed to whichever is most recent) and capped at
   LEDGER_MAX_RECORDS entries every time it is written. The cap is
   generous relative to what this store's live merchandising graph can
   ever actually produce (5 reciprocal Class A pairs + 2 Class B compound
   rules = at most ~12 distinct source->target relationships exist at
   all, in total, across the entire graph, so no real cart can ever need
   more simultaneously-active records than that) -- it exists purely as a
   defensive ceiling against a corrupted or adversarially-large attribute
   value, not as a limit realistic shopping behaviour should ever
   approach. This is a short-lived cart-level cooldown ledger, not a
   customer purchase-history record: it is never read for anything other
   than "is this exact relationship inside its 3-hour window right now,"
   nothing is retained past that window's expiry, and it carries no
   product titles, prices, handles, variant data or presentation data --
   only the two identifiers already authoritative elsewhere in this file
   plus one integer timestamp.

   Same-document UX after a successful add: this file's own upsell add
   never dispatches `cart:updated` (see the architecture note atop this
   file) and never calls `recalculate()` itself, so nothing re-evaluates
   the module synchronously after a successful add regardless of this
   stage's changes -- there was never an "instant replacement flash" risk
   to specifically guard against beyond what already existed. This stage
   still calls `clearRoot()` immediately on success (unchanged from Stage
   1.5) so the just-used card does not linger, and lets the NEXT real,
   naturally-occurring lifecycle event (drawer reopen, a `cart:updated`
   the THEME's own AJAX cart fires for some other reason, etc.) decide
   whether a different, still-eligible relationship has anything to show
   -- no timer, no polling, no artificial delay of any kind is introduced.
   ========================================================================== */
(function () {
  'use strict';

  // Defensive singleton guard. In normal operation this file is loaded once
  // via a single cached <script src> tag and #CartUpsell is never
  // re-created (see the mount-point note above), so this should never
  // actually trigger -- kept as a cheap, harmless safety net rather than
  // load-bearing, exactly like the equivalent guard pattern already used
  // elsewhere in this theme's own inline component scripts.
  if (window.__tymlusCartUpsellInit) return;
  window.__tymlusCartUpsellInit = true;

  var root = document.getElementById('CartUpsell');
  if (!root) return;

  var ATTR_KEY = '_tymlus_upsell_completed';

  // V2 -- see the "STAGE 4 / V2" header addendum above for the full design.
  // The sole two tunables the relationship-scoped cooldown ledger depends
  // on. COOLDOWN_MS also doubles as the future-timestamp sanity bound in
  // parseLedger() below, so a corrupted/malformed record can never
  // suppress a relationship for longer than one real, valid record ever
  // could.
  var COOLDOWN_MS = 10800000; // 3 hours, approved value -- exact ms equivalent
  var LEDGER_MAX_RECORDS = 20; // defensive ceiling only; this store's live graph can never produce more than ~12 distinct relationships at once (5 reciprocal Class A pairs + 2 Class B rules) -- see header addendum

  /* ------------------------------------------------------------------
     Existing-drawer refresh after a successful upsell-module add
     (resolves the one open architecture question from Section I/M).

     Empirically confirmed against the live Theme 8.5 preview before
     writing this: the theme's OWN "Add to bag" button and its OWN
     quantity +/- controls both refresh the drawer via exactly two
     calls -- POST to mutate the cart, then GET /cart?t=<ts>&view=ajax
     -- and that GET's response body is the exact HTML this theme uses
     to replace [data-products]'s content (a single
     <div class="cart__items" data-count="N" data-cart-subtotal="CENTS">
     fragment). That refresh step turned out to be wired directly into
     the theme's own per-instance click handlers (verified: a synthetic
     click on an existing, already-bound quantity button DOES trigger
     it; a freshly-constructed, dynamically-inserted form/button does
     NOT), so it cannot be reused directly for a brand-new product this
     module adds -- this module has to perform the same two-step
     refresh itself, using only the theme's own real endpoints and its
     own real, already-loaded money-format configuration (never a
     guessed format):
       1. GET /cart?t=<ts>&view=ajax (the theme's own real, working,
          read-only line-items endpoint) -> replace [data-products]'s
          content with the returned fragment, exactly as the theme's
          own handler does.
       2. Format that same fragment's data-cart-subtotal (cents,
          already present on it -- no extra fetch) using
          window.theme.settings.moneyFormat, which
          layout/theme.liquid sets from the store's real
          shop.money_format -- confirmed live as "GBP{{amount_no_decimals}}"
          -- and write the result into [data-subtotal], the exact node
          snippets/cart-drawer.liquid renders as
          {{ cart.total_price | money }}. This never invents a format;
          it reproduces the merchant's own configured one.
     Only these two existing nodes are ever touched, and only right
     after this module's own successful add -- never on the drawerOpen/
     cart:updated recalculation path, which never needs it because
     those are only ever fired by actions the theme already refreshes
     itself.
  ------------------------------------------------------------------ */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
    var value = '';
    function defaultTo(n, d) { return (n == null || isNaN(n)) ? d : n; }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number)) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return format;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function refreshExistingDrawerDisplay() {
    // Best-effort only: this exists purely to keep the pre-existing,
    // do-not-touch drawer visually correct after THIS module's own
    // mutation. If it fails for any reason, fail silently -- the next
    // real drawerOpen/cart:updated recalculation this module (or the
    // theme itself) performs will naturally reflect correct data; a
    // transient stale line-item list is far preferable to risking any
    // visible corruption of the do-not-touch drawer.
    return fetch('/cart?t=' + Date.now() + '&view=ajax', { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var freshItems = tmp.querySelector('.cart__items');
        if (!freshItems) return;
        var productsNode = document.querySelector('#CartDrawer [data-products]');
        if (productsNode) {
          productsNode.innerHTML = freshItems.outerHTML;

          // STAGE 2B.1 FIX -- restore the theme's OWN cart quantity/removal
          // control bindings on the fresh nodes this innerHTML swap just
          // created. theme.js's CartForm only re-applies those bindings via
          // its private reInit()->initQtySelectors() after ITS OWN
          // [data-products] rebuild (buildCart -> cartMarkup -> reInit); this
          // module's own innerHTML swap above bypasses that private path
          // entirely (CartForm's instance is never exposed globally, only
          // reachable via the theme's own 'ajaxProduct:added' event, which
          // this module deliberately does not fire -- see the comment above
          // refreshExistingDrawerDisplay()), orphaning theme.js's directly-
          // bound (non-delegated) js-qty__adjust listeners on every existing
          // line item, not just the one this module added. Confirmed
          // empirically (Stage 2B.1 investigation) that this silently broke
          // BOTH quantity changes AND cart-drawer removal for every item
          // already in the drawer, every time this function ran -- removal
          // in the drawer has no other control, since cart-item.liquid's
          // .cart__remove link is unconditionally display:none inside
          // .drawer via the theme's own pre-existing theme.css, so
          // quantity-to-zero is the ONLY removal path there, and it goes
          // through this exact listener.
          //
          // The fix re-runs the theme's own theme.QtySelector binding --
          // the identical construct and options CartForm.initQtySelectors()
          // itself uses (theme.js, confirmed via theme.CartForm's source) --
          // on the fresh .js-qty__wrapper nodes. This is not a new removal
          // system: it is the theme's own class, applied the theme's own
          // way, merely triggered from here because this module's DOM swap
          // is what orphaned the previous bindings. The still-alive
          // document-level 'cart:quantity.cart-cart-drawer' listener
          // (bound once by CartForm.init(), never destroyed by innerHTML
          // swaps since it lives on `document`, not on the swapped nodes)
          // picks up the events these fresh QtySelector instances dispatch,
          // exactly as it does for the theme's own native quantity changes.
          if (window.theme && typeof window.theme.QtySelector === 'function') {
            var drawerFormEl = document.getElementById('CartDrawerForm');
            var qtyNamespace = '.cart-' + ((drawerFormEl && drawerFormEl.dataset.location) || 'cart-drawer');
            var qtyWrappers = productsNode.querySelectorAll('.js-qty__wrapper');
            for (var qi = 0; qi < qtyWrappers.length; qi++) {
              try {
                new window.theme.QtySelector(qtyWrappers[qi], { namespace: qtyNamespace, isCart: true });
              } catch (e) { /* best-effort, see comment above refreshExistingDrawerDisplay() */ }
            }
          }
        }

        var subtotalCents = freshItems.getAttribute('data-cart-subtotal');
        var subtotalNode = document.querySelector('#CartDrawer [data-subtotal]');
        if (subtotalNode && subtotalCents !== null && !isNaN(Number(subtotalCents))) {
          subtotalNode.textContent = formatMoney(Number(subtotalCents));
        }

        // Empirically confirmed necessary, not optional: the theme's cart
        // item images use its own AOS ("image-fade-in") reveal -- opacity:0
        // by default in CSS until an ancestor picks up an .aos-animate
        // class. AOS only tracks elements it already knows about, so a raw
        // innerHTML swap creates real, fully-loaded <img> elements that
        // nonetheless stay invisible forever (verified: they load
        // successfully over the network, but sit at opacity:0) unless AOS
        // is explicitly told to re-scan. refreshHard() (not refresh(), which
        // only repositions elements AOS already tracks) is confirmed to be
        // the one that picks up brand-new nodes and reveals them
        // immediately since they're already on-screen. This reuses the
        // theme's own real library rather than reimplementing the reveal.
        if (window.AOS && typeof window.AOS.refreshHard === 'function') {
          window.AOS.refreshHard();
        }
      })
      .catch(function () { /* best-effort, see comment above */ });
  }

  /* ------------------------------------------------------------------
     STAGE 1.9 -- PRODUCTION MERCHANDISING GRAPH (Tier -> Recency ->
     Priority). See the Stage 1.9 addendum in the header comment above.

     The relationship graph itself (which products pair with which) is
     rendered once per page load by snippets/cart-upsell.liquid into the
     #TymlusUpsellGraph JSON tag, sourced live from
     custom.cart_upsell_priority (Class A) and the
     tymlus_upsell_compound_rule metaobject (Class B) -- this file reads
     that tag and contains no hardcoded product IDs of its own for the
     graph edges themselves.

     Stage 3.2 -- ATTACH_RATE removed. The hardcoded ATTACH_RATE table and
     each candidate's `strength` field (originally the TERTIARY tie-break
     for two qualifying Class A sources with indistinguishable recency)
     were removed here. Stage 3.1 had already made `strength` dead for
     ordering purposes -- the explicit per-edge `priority` field (read
     from the cart_upsell_priority metafield's own list order) fully
     replaced it as the same-source tie-break, and the comparator below
     stopped reading `strength` at that point. Removal was deferred to
     this separate stage per the approved staged sequence, and only
     carried out after live testing (Stage 3.1 checkpoint: a temporary
     2-target metafield value, live in the cart drawer) proved (a)
     multi-target priority ordering picks the lowest-priority eligible
     target, and (b) an ineligible target-1 correctly falls through to
     target-2 -- with `strength` never read anywhere except its own
     assignment, so its removal changes no currently reachable
     recommendation outcome. Cross-source precedence is still decided
     entirely by Tier then Recency, both unaffected by this change; a
     merchant's cart_upsell_priority metafield shape and every other
     module behaviour are unchanged.
  ------------------------------------------------------------------ */

  var TIER_A = 1; // Complete an incomplete matching set (spec Section 3)
  var TIER_B = 2; // Full-tracksuit next-step, merchant rule (spec Section 3)
  // Tier C is reserved and intentionally has zero entries in V1 (spec
  // Section 2.3) -- no TIER_C candidates are ever produced by this file.

  function readGraph() {
    var node = document.getElementById('TymlusUpsellGraph');
    if (!node) return { classA: [], classB: [] };
    try {
      var parsed = JSON.parse(node.textContent || node.innerText || '{}');
      return {
        classA: Array.isArray(parsed.classA) ? parsed.classA : [],
        classB: Array.isArray(parsed.classB) ? parsed.classB : []
      };
    } catch (e) {
      return { classA: [], classB: [] }; // malformed/missing graph data -- fail to "no recommendation", never throw
    }
  }
  var GRAPH = readGraph();

  // Stage 1 placeholder convention for "deliberately excluded from cart
  // recommendations" (Section D) -- a product tagged with this exact tag
  // (case-insensitive) is rejected as a candidate regardless of ladder
  // position. Real system: a boolean/list field alongside the priority
  // metafield; documented in the Stage 1 report rather than built here,
  // since no such data exists yet and inventing it would be scope creep.
  var EXCLUDE_TAG = 'cart-upsell-exclude';

  /* ------------------------------------------------------------------
     Analytics event architecture (Section K). Dispatches plain
     CustomEvents on `document` -- consumed by whatever dataLayer/GTM
     bridge already exists in this theme (none was found wired up during
     Stage 1's audit; Stage 2 can attach a listener without any change
     here). Every dispatch goes through this one function so de-duplication
     lives in one place.
  ------------------------------------------------------------------ */
  var firedOnce = {}; // event-name -> true, for events that must fire at most once per cart lifecycle (viewed, completed)
  function track(name, payload, onceKey) {
    if (onceKey) {
      if (firedOnce[onceKey]) return;
      firedOnce[onceKey] = true;
    }
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: payload || {} }));
    } catch (e) { /* never let analytics dispatch break the engine */ }
  }

  /* ------------------------------------------------------------------
     State machine -- V2, simplified.

     STATE_COMPLETED and STATE_CONVERTED_PENDING (Stage 1.5) are RETIRED as
     of Stage 4/V2: both existed solely to make `recalculate()` abort the
     WHOLE module once any relationship completed, which is precisely the
     global-suppression defect V2 replaces with the relationship-scoped
     cooldown ledger (see the header addendum and `reconcileLedger()`/
     `isRelationshipCoolingDown()` below). STATE_ADDING is unchanged and
     still the only state `recalculate()` itself needs to check: it freezes
     the module against recalculation for the duration of one in-flight
     add-to-bag request, exactly as before (Stage 3.3's is-busy protection,
     the double-tap guard, and the failure-path revert-to-AVAILABLE are all
     untouched). Once an add resolves -- success or failure -- `state`
     always returns to AVAILABLE; the relationship-scoped exclusion that
     follows a SUCCESSFUL add lives in the cooldown ledger, not in `state`.
  ------------------------------------------------------------------ */
  var STATE_AVAILABLE = 'available';
  var STATE_ADDING = 'adding';
  var state = STATE_AVAILABLE;

  var currentCandidate = null;   // the resolved, fetched product JSON currently rendered, or null
  var currentSourceId = null;    // product_id the current candidate was chosen because of
  var recalcToken = 0;           // increments on every recalculation; a stale in-flight async resolve checks this and bails rather than racing a newer one
  var productCache = {};         // handle -> fetched /products/<handle>.js JSON for the CURRENT resolveCandidate() pass only -- reset at the start of every pass (see resolveCandidate) so availability/exclusion is re-checked at each natural graph evaluation point, per FINAL LOCKED spec Section 5/6

  var activeLedger = [];         // V2 -- the parsed, expiry-filtered set of {source,target,ts} cooldown records currently in effect; recomputed by reconcileLedger() at the top of every recalculate() pass, consulted by buildCandidates() below
  var sessionPendingCooldowns = []; // V2 -- records written the instant an add succeeds, before /cart/update.js confirms persistence -- covers the narrow race where this same document re-evaluates before the durable write lands; merged into activeLedger by reconcileLedger()

  function fetchJSON(method, url, body) {
    var opts = { method: method, headers: {}, credentials: 'same-origin' };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, status: res.status, json: json };
      });
    });
  }

  function fetchProductByHandle(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) return null; // 404/unavailable -- treated as "not genuinely purchasable" by the caller
      return res.json();
    }).then(function (json) {
      if (json) productCache[handle] = json;
      return json;
    }).catch(function () { return null; });
  }

  // V2 -- parses the cart attribute's ledger value into a clean array of
  // { source, target, ts } records, fail-open on every possible way the
  // value could be wrong. Absent/empty/legacy-"true"/malformed-JSON/non-
  // array/malformed-record/non-numeric-timestamp/implausible-future-
  // timestamp/expired all resolve to "no active cooldown from this
  // record" -- never to global suppression, and never to a thrown
  // exception. A single bad record inside an otherwise-valid array is
  // dropped individually; it does not invalidate the rest of the ledger.
  function parseLedger(raw) {
    if (typeof raw !== 'string' || !raw) return [];
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return []; // malformed JSON -- fail open
    }
    if (!Array.isArray(parsed)) return []; // legacy "true" (parses to boolean true) or any other non-array shape -- fail open
    var now = Date.now();
    var out = [];
    for (var i = 0; i < parsed.length; i++) {
      var rec = parsed[i];
      if (!Array.isArray(rec) || rec.length < 3) continue; // malformed record -- drop just this one
      var sourceId = rec[0] === 0 ? '0' : String(rec[0] || '');
      var targetId = rec[1] === 0 ? '0' : String(rec[1] || '');
      var ts = rec[2];
      if (!sourceId || !targetId) continue;
      if (typeof ts !== 'number' || !isFinite(ts)) continue; // non-numeric timestamp -- drop, fail open
      // Clock-skew/corruption guard: never trust a timestamp more than a
      // few minutes in the future -- without this bound, a corrupted or
      // adversarial timestamp could suppress a relationship far longer
      // than the one legitimate 3-hour window this system ever grants.
      if (ts > now + 300000) continue;
      if (now - ts >= COOLDOWN_MS) continue; // expired -- simply omitted, not an error
      out.push({ source: sourceId, target: targetId, ts: ts });
    }
    return out;
  }

  // V2 -- dedupe (keep the most recent record per exact source->target
  // pair) and cap (keep the most recent LEDGER_MAX_RECORDS overall).
  // Applied both when computing this pass's effective activeLedger and
  // again, separately, immediately before every persistence write (see
  // handleAddAttempt()) -- "prune again before persisting," per the V2
  // spec, so a long-lived cart's attribute never grows unbounded.
  function dedupeAndCapLedger(records) {
    var bySourceTarget = {};
    for (var i = 0; i < records.length; i++) {
      var rec = records[i];
      var key = rec.source + '>' + rec.target;
      if (!bySourceTarget[key] || rec.ts > bySourceTarget[key].ts) {
        bySourceTarget[key] = rec;
      }
    }
    var out = [];
    for (var k in bySourceTarget) {
      if (Object.prototype.hasOwnProperty.call(bySourceTarget, k)) out.push(bySourceTarget[k]);
    }
    out.sort(function (a, b) { return b.ts - a.ts; }); // most recent first
    if (out.length > LEDGER_MAX_RECORDS) out = out.slice(0, LEDGER_MAX_RECORDS);
    return out;
  }

  function serializeLedger(records) {
    return JSON.stringify(records.map(function (r) { return [r.source, r.target, r.ts]; }));
  }

  function isRelationshipCoolingDown(records, sourceId, targetId) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].source === sourceId && records[i].target === targetId) return true;
    }
    return false;
  }

  // V2 -- the sole read path. Replaces reconcileState()'s global
  // COMPLETED/CONVERTED_PENDING verdict with the effective set of
  // currently-cooling-down relationships for THIS evaluation pass:
  // the durable, cart-attribute-backed ledger (source of truth across
  // refresh/navigation, exactly as the Stage 1 attribute was) merged with
  // any not-yet-persisted record this same document wrote a moment ago
  // (sessionPendingCooldowns -- see its declaration above). Populates the
  // module-level activeLedger that buildCandidates() consults below; does
  // not itself decide whether to show or hide anything.
  function reconcileLedger(cart) {
    var persisted = parseLedger(cart && cart.attributes && cart.attributes[ATTR_KEY]);
    var now = Date.now();
    sessionPendingCooldowns = sessionPendingCooldowns.filter(function (r) { return now - r.ts < COOLDOWN_MS; });
    activeLedger = dedupeAndCapLedger(persisted.concat(sessionPendingCooldowns));
  }

  /* ------------------------------------------------------------------
     Candidate resolution (Stage 1 Sections C, D, G; FINAL LOCKED spec
     Sections 3, 3.1, 4, 5, 6, 7)
  ------------------------------------------------------------------ */

  // recencyIndexOf: position of a product's line item within cart.items --
  // index 0 is the most recently newly-added distinct line item (see the
  // empirical verification note at the top of this file, unchanged since
  // Stage 1/1.5). A LOWER index means MORE recent. This is the engine's
  // sole recency signal; Stage 1.9 widens its scope from "the single most
  // recent product only" to "every product currently in the cart", since
  // the locked spec requires evaluating antecedents across the whole cart.
  function recencyIndexOf(productId, cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].product_id) === productId) return i;
    }
    return -1; // not in cart; callers never sort on this case
  }

  // Builds the full, precedence-ordered candidate list for the CURRENT
  // cart (spec Section 6). Only the cheap "target already in cart" filter
  // (Section 5's first bullet) is applied here; availability/exclusion/
  // gift-card filtering (the rest of Section 5) needs a network fetch per
  // candidate and happens in resolveCandidate() below, walked in this
  // exact order -- so a filtered-out top candidate naturally falls
  // through to the next one, which is precisely the structural fallback
  // Section 7 requires, with no separate "retry" path needed.
  function buildCandidates(cart) {
    var cartProductIds = {};
    cart.items.forEach(function (item) { cartProductIds[String(item.product_id)] = true; });

    var candidates = [];

    // Class A: single-antecedent matching-set completion (spec 2.1, 3).
    GRAPH.classA.forEach(function (edge) {
      var sourceId = String(edge.source);
      var targetId = String(edge.target);
      if (!cartProductIds[sourceId]) return; // antecedent not present
      if (cartProductIds[targetId]) return;  // set already complete -- filtered per Section 5
      // V2 -- relationship-scoped cooldown exclusion. Architecturally the
      // same kind of per-candidate rejection as the already-in-cart check
      // immediately above: this candidate alone is skipped, the loop
      // continues to every other edge untouched, so an unrelated Class A
      // or Class B relationship is never affected by this one's cooldown.
      if (isRelationshipCoolingDown(activeLedger, sourceId, targetId)) return;
      candidates.push({
        tier: TIER_A,
        sourceId: sourceId,
        targetId: targetId,
        targetHandle: edge.targetHandle,
        recency: recencyIndexOf(sourceId, cart),
        // Stage 3.1: explicit per-edge priority, taken from the
        // cart_upsell_priority metafield list's own item order (see
        // cart-upsell.liquid's classA serialization). Only ever
        // meaningful as a tie-break among multiple edges sharing the
        // SAME source -- distinct sources always have distinct recency
        // indices (see recencyIndexOf()), so priority never overrides
        // Tier/Recency precedence between different sources. Defaults to
        // 0 defensively if an older/malformed graph tag lacks the field.
        priority: (typeof edge.priority === 'number') ? edge.priority : 0
      });
    });

    // Class B: two-antecedent compound rule (spec 2.2, 3).
    GRAPH.classB.forEach(function (rule) {
      var sources = (rule.sources || []).map(String);
      var targetId = String(rule.target);
      if (sources.length < 2) return; // malformed entry -- skip defensively
      var allPresent = sources.every(function (sid) { return cartProductIds[sid]; });
      if (!allPresent) return;        // trigger requires BOTH antecedents present
      if (cartProductIds[targetId]) return; // already present -- filtered per Section 5
      // "most_recent_antecedent_added_at" (spec Section 6): the MORE
      // recently added of the two antecedents decides this candidate's
      // recency and is also what tymlus_upsell_* analytics attributes
      // this recommendation to as source_product_id.
      var sortedSources = sources.slice().sort(function (a, b) {
        return recencyIndexOf(a, cart) - recencyIndexOf(b, cart);
      });
      // V2 -- relationship-scoped cooldown exclusion, identical position
      // and reasoning as the Class A check above. Identity is keyed on
      // exactly the (sourceId, targetId) pair this candidate is built
      // with -- sortedSources[0] here, same as the candidate object pushed
      // below and the same pair handleAddAttempt() records on completion
      // -- so this suppresses only the specific compound relationship just
      // completed, never the whole Class B rule and never the target
      // product considered on its own (the same target id can legitimately
      // appear in an unrelated, still-eligible Class A edge -- see the
      // header addendum). Note: if the two antecedents' relative cart
      // recency order changes between completion and a later evaluation,
      // sortedSources[0] can differ from what was recorded, which would
      // fail OPEN (the relationship becomes eligible slightly early)
      // rather than fail closed -- consistent with every other fail-open
      // guarantee in this stage, and disclosed here rather than silently
      // relied upon.
      if (isRelationshipCoolingDown(activeLedger, sortedSources[0], targetId)) return;
      candidates.push({
        tier: TIER_B,
        sourceId: sortedSources[0],
        targetId: targetId,
        targetHandle: rule.targetHandle,
        recency: recencyIndexOf(sortedSources[0], cart),
        // Class B is explicitly NOT same-cart-validated (spec Section 2.2)
        // -- it never wins a priority tie-break against real Class A
        // evidence, since priority only ever breaks a tie among
        // candidates sharing the same source and Class B's synthetic
        // priority of 0 is never compared against Class A's real
        // per-edge values in a same-source tie (different tiers sort
        // first). Harmless in V1 regardless: Tier B can only ever
        // produce one live candidate at a time (see Collision #6, where
        // both Class B targets are simultaneously present and therefore
        // both filtered out before precedence ranking).
        priority: 0
      });
    });

    // Tier -> Recency -> Priority (spec Sections 3.1, 4, 6, extended by
    // Stage 3.1). Lower tier number wins; within a tier, lower recency
    // index (= more recently added) wins; priority only breaks a
    // same-source tie (multiple ordered targets for one antecedent) --
    // in every case this reaches today, two candidates are only ever
    // tied on recency when they share a source, since distinct sources
    // always occupy distinct cart-array indices (recencyIndexOf()).
    // Ascending priority (0 first) matches "first eligible candidate in
    // priority order wins," with resolveCandidate()'s existing
    // unchanged fall-through walk supplying the "eligible" part.
    candidates.sort(function (a, b) {
      if (a.tier !== b.tier) return a.tier - b.tier;
      if (a.recency !== b.recency) return a.recency - b.recency;
      return a.priority - b.priority;
    });

    return candidates;
  }

  function productIsExcluded(product) {
    if (!product.tags) return false;
    var tags = Array.isArray(product.tags) ? product.tags : String(product.tags).split(',');
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).trim().toLowerCase() === EXCLUDE_TAG) return true;
    }
    return false;
  }

  function productLooksLikeGiftCard(product) {
    // Best-effort client-side signal only -- see the Stage 1 report for why
    // an authoritative check needs either the real metafield-driven
    // candidate list to be pre-filtered server-side, or a future
    // Admin-authenticated lookup. None of the Stage 1 test products are
    // gift cards, so this never actually triggers in Stage 1 testing.
    return typeof product.product_type === 'string' && product.product_type.toLowerCase() === 'gift card';
  }

  function productHasPurchasableVariant(product) {
    if (!product.variants || !product.variants.length) return false;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return true;
    }
    return false;
  }

  // Walks the precedence-ordered candidate list from buildCandidates() and
  // resolves the FIRST one that survives every filter in Section 5
  // (available, not excluded, not a gift card, has a purchasable variant).
  // Same cascading try/fallback shape as Stage 1's resolveCandidate() --
  // now fed by the full cross-cart, tier-ranked candidate list instead of
  // one source product's curated ladder. Returns a Promise<Object|null>;
  // null means "show no upsell" (Tier D -- true silence, spec Section 11).
  function resolveCandidate(cart) {
    // FINAL LOCKED spec Section 5/6: "current candidate availability...
    // checked at each point the recommendation graph naturally evaluates."
    // resolveCandidate() IS one such evaluation point (its sole caller is
    // the recalculation handler below), so productCache must not carry a
    // fetched product's availability/exclusion/gift-card status across
    // separate resolveCandidate() invocations -- only within this single
    // pass (e.g. if the same handle appears as more than one candidate
    // here). Caching across passes would mean a product excluded or made
    // unavailable mid-session keeps being recommended until a full page
    // reload, which contradicts "no memory of what was previously shown"
    // (spec Section 6) applied to availability specifically. This is a
    // one-line staleness fix, not a design change -- no background polling
    // is introduced; the fetch still only happens on a real trigger.
    productCache = {};
    var ordered = buildCandidates(cart);
    if (!ordered.length) return Promise.resolve(null);

    var i = 0;
    function tryNext() {
      if (i >= ordered.length) return Promise.resolve(null);
      var candidate = ordered[i++];
      if (!candidate.targetHandle) return tryNext(); // defensive; malformed graph entry

      return fetchProductByHandle(candidate.targetHandle).then(function (product) {
        if (!product) return tryNext();                      // inactive/unavailable (404) or fetch failure
        if (productLooksLikeGiftCard(product)) return tryNext();
        if (productIsExcluded(product)) return tryNext();
        if (!productHasPurchasableVariant(product)) return tryNext();
        return { productId: candidate.targetId, rank: i, product: product, sourceId: candidate.sourceId };
      });
    }
    return tryNext();
  }

  /* ------------------------------------------------------------------
     Rendering -- STAGE 2B presentation seam.

     snippets/cart-upsell.liquid now pre-renders one hidden
     .tymlus-upsell__card per possible candidate product (every Class A
     product and every Class B target -- confirmed to be the same set;
     see the comment at the top of that file) at page-load time, using
     the theme's own native components (image-element, product.price,
     element.button) and existing typography/color tokens. Nothing about
     candidate resolution, the Tier -> Recency -> Strength engine,
     completion semantics or analytics event names/payloads changes here
     -- this section only selects which pre-rendered card (if any) is
     shown, resets it to a clean state, and syncs this pass's freshly-
     fetched live variant availability onto it. handleAddAttempt() below
     receives the card's <fieldset> in the same `select` parameter
     position a Stage 1.9 <select> used to occupy.

     Stage 3.3 note: handleAddAttempt() originally froze this fieldset via
     plain `.disabled = true/false`, relying on the HTML spec's native
     cascade to every contained radio <input>. That was withdrawn after a
     real-device defect report: this stylesheet's own genuine-sold-out
     rule is keyed off `input:disabled` (see the comment above that rule,
     below), so the native cascade made every size -- including genuinely
     available ones -- flash the sold-out crossed-out treatment for the
     full duration of the add-to-cart network round trip. See the
     `is-busy` class (added at the same two call sites below) and its
     accompanying CSS rule for the fix and full root-cause writeup.
  ------------------------------------------------------------------ */
  function money(cents) {
    return formatMoney(cents);
  }

  var cardByProductId = {};   // product id string -> { el, fieldset, addBtn, statusLine, resolved, sourceProductId, lastFiredSizeSelectValue }
  var currentCardEntry = null; // the entry currently shown, or null

  function resetCardEntry(entry) {
    entry.el.hidden = true;
    entry.resolved = null;
    entry.sourceProductId = null;
    entry.lastFiredSizeSelectValue = null;
    entry.statusLine.textContent = '';
    var radios = entry.fieldset.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) { radios[i].checked = false; }
    entry.fieldset.classList.remove('is-busy'); // Stage 3.3 -- see the header comment above money()
    entry.addBtn.disabled = true;
    // Stage 2C: the CTA is only ever revealed by the 'is-visible' class
    // (see .tymlus-upsell__add's max-height:0 base rule) -- a reset must
    // clear it back to the zero-reserved-height pre-selection state, and
    // reset its price-inclusive text back to the plain default so the
    // next candidate to render into this same pre-rendered card doesn't
    // briefly show a stale price before a size is picked.
    entry.addBtn.classList.remove('is-visible');
    entry.addBtn.textContent = 'Add to bag';

    // Stage 2D: return the media carousel to its first slide and reset
    // the progress indicator whenever this card is hidden/reset, so the
    // next time a candidate renders into it the shopper always starts on
    // the featured image again, never mid-swipe from a previous view.
    // Stage 2L: the track's position is now owned by the Flickity instance
    // (entry.slideshow), not a plain scrollLeft this file sets directly --
    // select(0, false, true) is Flickity's own real, confirmed API for an
    // instant (non-animated) reposition (isWrapped=false, isInstant=true).
    if (entry.slideshow) {
      entry.slideshow.slideshow.select(0, false, true);
    }
    if (entry.progressFill) {
      entry.progressFill.style.transform = 'translateX(0%)';
    }
  }

  function clearRoot() {
    if (currentCardEntry) {
      resetCardEntry(currentCardEntry);
      currentCardEntry = null;
    }
    root.hidden = true;
  }

  // Stage 1.5, Objective 2: tymlus_upsell_size_selected. Fires only on a
  // genuine shopper selection of a real size/variant -- never merely
  // because a card starts out with a checked radio (it never does --
  // renderCandidate() below explicitly unchecks every radio on every
  // (re)render, so no 'change' event fires until the shopper actually
  // picks something). entry.lastFiredSizeSelectValue is reset to null
  // every time a fresh candidate renders into this card, so re-selecting
  // the same size after genuinely changing away and back correctly fires
  // again -- while native radio 'change' semantics already mean clicking
  // the currently-checked radio a second time in a row doesn't fire
  // 'change' at all, so no extra de-dup is needed for that case (same
  // reasoning as the Stage 1.5 <select> version this replaces).
  function onSizeChange(entry) {
    var checked = entry.fieldset.querySelector('input[type="radio"]:checked');
    entry.addBtn.disabled = !checked;

    var selectedVariant = null;
    if (checked && entry.resolved) {
      var variants = entry.resolved.product.variants;
      for (var vi = 0; vi < variants.length; vi++) {
        if (String(variants[vi].id) === checked.value) { selectedVariant = variants[vi]; break; }
      }
    }

    // Stage 2C: the CTA has zero reserved height in the pre-selection
    // layout (see .tymlus-upsell__add's base max-height:0 rule) -- adding
    // 'is-visible' is what reveals it, flush beneath the sizes, via a
    // smooth max-height/opacity/padding transition (no bounce/spring).
    // This runs on every genuine change event, independent of the
    // tymlus_upsell_size_selected de-dup below, so switching between two
    // different available sizes always updates the displayed price to
    // match the real selected variant -- never a hardcoded figure.
    if (checked && selectedVariant) {
      entry.addBtn.textContent = 'Add to bag - ' + money(selectedVariant.price);
      entry.addBtn.classList.add('is-visible');
    } else {
      entry.addBtn.classList.remove('is-visible');
    }

    if (!checked || checked.value === entry.lastFiredSizeSelectValue) return;
    entry.lastFiredSizeSelectValue = checked.value;
    if (!entry.resolved) return; // defensive -- should not happen, a change can only fire on a currently-shown card
    track('tymlus_upsell_size_selected', {
      recommended_product_id: entry.resolved.productId,
      recommended_product_handle: entry.resolved.product.handle,
      selected_variant_id: Number(checked.value),
      selected_option_value: selectedVariant ? selectedVariant.title : null,
      source_product_id: entry.sourceProductId
    });
  }

  /* ------------------------------------------------------------------
     Stage 2D -- media carousel progress indicator.

     [Historical note, mechanism superseded Stage 2L below: this was
     originally written when the swipe itself was pure CSS scroll-snap
     and this listener only ever read the track's own native scrollLeft.
     The progress indicator's own visible behaviour -- a thin fill segment
     that tracks the current slide, hidden entirely for single-image
     candidates -- is unchanged; only what feeds it changed.]
  ------------------------------------------------------------------ */
  // Stage 2L -- direct reuse of the PDP product gallery's own carousel
  // engine. See the header comment block above for the full audit/decision
  // writeup. This function used to build two synthesized DOM clone slides
  // and drive a bespoke scrollLeft/scroll-snap loop by hand; it now simply
  // hands the track to the theme's own theme.Slideshow class (a thin,
  // already-reusable wrapper around the real Flickity library, confirmed
  // traced directly from assets/theme.js -- not re-implemented here), the
  // exact same class theme.Product.prototype.initProductSlider instantiates
  // for the PDP gallery. Flickity owns swipe physics, boundary looping
  // (wrapAround: true, its own internal/managed clone cells -- never DOM
  // nodes this file needs to track), current-slide detection, and iOS
  // touch/momentum handling entirely itself; this file only listens for
  // its authoritative 'change' index (via theme.Slideshow's existing
  // args.callbacks.onChange extension point) to keep the progress-fill
  // element in sync.
  function wireMediaCarousel(cardEl, entry) {
    var mediaTrack = cardEl.querySelector('[data-tymlus-media-track]');
    var progressWrap = cardEl.querySelector('[data-tymlus-progress]');
    var progressFill = cardEl.querySelector('[data-tymlus-progress-fill]');
    if (!mediaTrack) return;

    entry.mediaTrack = mediaTrack;
    entry.progressFill = progressFill;
    entry.slideshow = null;

    var realSlides = mediaTrack.querySelectorAll('[data-tymlus-media-slide]');
    var slideCount = realSlides.length;
    if (!progressWrap || !progressFill || slideCount < 2) {
      if (progressWrap) progressWrap.hidden = true;
      return;
    }

    progressWrap.hidden = false;
    progressFill.style.width = (100 / slideCount) + '%';

    function updateProgress(index) {
      progressFill.style.transform = 'translateX(' + (index * 100) + '%)';
    }

    // Defensive only -- vendor-scripts-v11.js (Flickity) and theme.js
    // (theme.Slideshow) are both confirmed loaded unconditionally, sitewide,
    // in <head> (layout/theme.liquid), i.e. wherever the cart drawer that
    // hosts this card can open. If that ever isn't true for some reason,
    // fail to a static, non-interactive first slide with the progress
    // indicator hidden -- "prefer rendering nothing over rendering a wrong
    // recommendation" (Section K) applies equally to a broken interactive
    // control.
    if (!window.theme || typeof window.theme.Slideshow !== 'function') {
      progressWrap.hidden = true;
      return;
    }

    // Configuration intentionally minimal: everything not overridden here
    // is theme.Slideshow's own default, which already includes
    // wrapAround: true, initialIndex: 0, pageDots: false and
    // prevNextButtons: false -- the same defaults the PDP gallery starts
    // from before layering its own product-specific overrides. This card's
    // only two overrides both exist because this card's locked requirements
    // genuinely differ from a product gallery's:
    //   - adaptiveHeight: false -- keeps the fixed aspect-ratio box (Stage
    //     2D/2I) so the overlaid size-selector/CTA never move between
    //     slides. PDP's own gallery deliberately does the opposite
    //     (adaptiveHeight: true) because it has no such overlay.
    //   - setGallerySize: false -- this card's box height is owned by CSS
    //     (aspect-ratio, set inline per candidate), not by Flickity
    //     measuring cell content -- confirmed safe with zero extra CSS
    //     because theme.css already ships sitewide
    //     .flickity-viewport{height:100%}/.flickity-slider{height:100%}
    //     base rules that this card's own .tymlus-upsell__media-slide CSS
    //     (width:100%;height:100%) already relies on.
    entry.slideshow = new window.theme.Slideshow(mediaTrack, {
      adaptiveHeight: false,
      setGallerySize: false,
      callbacks: {
        onChange: function (index) { updateProgress(index); }
      }
    });
  }

  function wireCandidateCards() {
    var cards = root.querySelectorAll('[data-tymlus-candidate]');
    for (var i = 0; i < cards.length; i++) {
      (function (cardEl) {
        var productId = cardEl.getAttribute('data-tymlus-candidate');
        var fieldset = cardEl.querySelector('[data-tymlus-size-fieldset]');
        var addBtn = cardEl.querySelector('[data-tymlus-add]');
        var statusLine = cardEl.querySelector('[data-tymlus-status]');
        if (!productId || !fieldset || !addBtn || !statusLine) return; // defensive; malformed markup

        var entry = {
          el: cardEl,
          fieldset: fieldset,
          addBtn: addBtn,
          statusLine: statusLine,
          resolved: null,
          sourceProductId: null,
          lastFiredSizeSelectValue: null,
          mediaTrack: null,
          progressFill: null,
          slideshow: null // Stage 2L -- set by wireMediaCarousel() to this card's theme.Slideshow (Flickity) instance, when it has >= 2 real slides
        };
        cardByProductId[productId] = entry;

        fieldset.addEventListener('change', function () { onSizeChange(entry); });

        addBtn.addEventListener('click', function () {
          if (state === STATE_ADDING) return; // freeze against duplicate submissions
          var checked = fieldset.querySelector('input[type="radio"]:checked');
          if (!checked || !entry.resolved) return;
          handleAddAttempt(entry.resolved, entry.sourceProductId, Number(checked.value), addBtn, fieldset, statusLine);
        });

        wireMediaCarousel(cardEl, entry);
      })(cards[i]);
    }
  }
  wireCandidateCards();

  function renderCandidate(resolved, sourceProductId) {
    var product = resolved.product;
    var entry = cardByProductId[String(resolved.productId)];

    if (!entry) {
      // Defensive only: every resolvable target is pre-rendered in
      // cart-upsell.liquid (see the comment at the top of that file), so
      // this should never actually trigger. Fail to "no visible
      // recommendation" rather than showing something incoherent, per
      // Section K's "prefer rendering nothing over rendering a wrong
      // recommendation".
      if (window.console && window.console.warn) {
        window.console.warn('[tymlus-upsell] no pre-rendered card found for product ' + resolved.productId);
      }
      currentCandidate = null;
      currentSourceId = null;
      clearRoot();
      return;
    }

    // Candidate replacement while the drawer is open (spec Section 7/L):
    // hide/reset whichever OTHER card was previously showing. An identical-
    // candidate re-render is already short-circuited by recalculate()'s own
    // de-dup check before this function is ever called, so reaching here
    // with currentCardEntry already set always means a genuinely different
    // candidate.
    if (currentCardEntry && currentCardEntry !== entry) {
      resetCardEntry(currentCardEntry);
    }
    currentCardEntry = entry;

    currentCandidate = resolved;
    currentSourceId = sourceProductId;
    entry.resolved = resolved;
    entry.sourceProductId = sourceProductId;
    entry.lastFiredSizeSelectValue = null;
    entry.statusLine.textContent = '';
    entry.fieldset.classList.remove('is-busy'); // Stage 3.3 -- see the header comment above money()

    // Sync this pass's freshly-fetched live variant availability onto the
    // pre-rendered radio inputs -- the markup itself was built from
    // page-load-time product data, so a variant that has sold out (or come
    // back in stock) since must be reflected here, exactly as Section 5/6
    // requires at every natural evaluation point. Every radio starts
    // unchecked on every (re)render, matching the placeholder-always-first
    // behaviour of the Stage 1.9 <select> this replaces.
    var radios = entry.fieldset.querySelectorAll('input[type="radio"][data-tymlus-size-input]');
    var liveVariantById = {};
    product.variants.forEach(function (v) { liveVariantById[String(v.id)] = v; });
    for (var i = 0; i < radios.length; i++) {
      var liveVariant = liveVariantById[radios[i].value];
      radios[i].disabled = !liveVariant || !liveVariant.available;
      radios[i].checked = false;
    }
    entry.addBtn.disabled = true;

    root.hidden = false;
    entry.el.hidden = false;

    // Stage 2G, Objective 5 (mechanism updated Stage 2L) -- the card has
    // real layout now that it is no longer hidden, so this is the first
    // point at which mediaTrack's own width is measurable. Unlike the PDP
    // gallery (always visible when its own Flickity instance is built),
    // this card's Flickity instance was constructed at wire-time while
    // still `hidden`, against a zero-width element -- resize() makes it
    // remeasure now that real layout exists, and the instant select(0)
    // (same API as resetCardEntry() above) guarantees the shopper always
    // starts on the real first slide, never mid-carousel from a previous
    // reveal of this same pre-rendered card.
    if (entry.slideshow) {
      entry.slideshow.resize();
      entry.slideshow.slideshow.select(0, false, true);
    }

    // See the AOS note above refreshExistingDrawerDisplay(): image-
    // element's own data-aos reveal is scanned by the same global AOS
    // library already relied on elsewhere in this file, not a bespoke
    // mechanism. A card's image can go from never-shown to shown well
    // after the drawer's own initial reveal sequence has already run
    // (e.g. a later candidate replacement), so it needs the exact same
    // re-scan nudge this file already gives its own post-add drawer
    // refresh, rather than assuming AOS notices on its own.
    if (window.AOS && typeof window.AOS.refreshHard === 'function') {
      window.AOS.refreshHard();
    }

    track('tymlus_upsell_viewed', {
      source_product_id: sourceProductId,
      recommended_product_id: resolved.productId,
      recommendation_rank: resolved.rank
    }, 'viewed:' + sourceProductId + ':' + resolved.productId);
  }

  /* ------------------------------------------------------------------
     AVAILABLE -> ADDING -> (AVAILABLE, relationship now cooling down)
     -- or back to AVAILABLE with nothing recorded if the add itself
     fails -- Sections A, J of Stage 1, Stage 1.5, refactored for V2.

     V2 change: a successful add no longer moves `state` into a global
     "converted pending" hold. It records this exact (source, target)
     relationship into sessionPendingCooldowns immediately (synchronously,
     before any further network call), then returns `state` straight to
     AVAILABLE. Nothing re-evaluates the module synchronously afterward
     regardless (see the header addendum), so this carries no risk of an
     instant second offer, while a genuinely different, unrelated
     relationship remains fully eligible on the very next natural
     recalculation.
  ------------------------------------------------------------------ */
  // Shared by both persistence-failure shapes below: a resolved-but-
  // unsuccessful /cart/update.js response, and a hard network-level
  // rejection (fetch() itself throwing) during that same call. Reported
  // via console.warn, not console.error/an exception, so it's visible
  // for diagnostics without counting as a console error.
  function warnPersistenceFailed() {
    if (window.console && window.console.warn) {
      window.console.warn('[tymlus-upsell] cart add succeeded and this relationship is excluded for the rest of this document (sessionPendingCooldowns), but the durable cart-attribute write failed. It will resolve from real cart state on the next page load or recalculation once the attribute reflects it.');
    }
  }

  function handleAddAttempt(resolved, sourceProductId, variantId, addBtn, select, statusLine) {
    state = STATE_ADDING; // freeze: no recalculation, no product swap, preserved visual state until this settles
    addBtn.disabled = true;
    // Stage 3.3: freeze the size fieldset against interaction WITHOUT the
    // native `disabled` attribute -- see the header comment above money()
    // for the real-device defect this replaces (native `.disabled` here
    // cascaded to every radio, and this stylesheet's genuine-sold-out
    // rule is keyed off `input:disabled`, so every size flashed sold-out
    // for the length of the add-to-cart request regardless of its real
    // stock state). `is-busy` sets pointer-events:none on the fieldset
    // only -- no color/border/content changes, so the existing valid
    // selection stays visually exactly as it was -- and blocks the same
    // click-driven interaction disabling the fieldset did, since the
    // fieldset's real clickable surface is the label (the radio itself is
    // a 1px, opacity:0 target -- see .tymlus-upsell__size-option
    // input[type='radio'] below) and pointer-events is inherited down to
    // it with no override in this stylesheet.
    select.classList.add('is-busy');
    statusLine.textContent = '';

    var cartValueBefore = null;
    var cartLineCountBefore = null; // Stage 2Q -- see the banner-refresh comment below
    var attributesBefore = null;    // V2 -- this cart's attributes as of the pre-add read, reused below to seed the ledger being persisted so an unrelated relationship's still-active cooldown already recorded on this cart is never dropped by this add's own persistence write

    fetchJSON('GET', '/cart.js').then(function (before) {
      cartValueBefore = before.json.total_price;
      cartLineCountBefore = before.json.items.length;
      attributesBefore = before.json.attributes;

      track('tymlus_upsell_add_attempt', {
        source_product_id: sourceProductId,
        recommended_product_id: resolved.productId,
        recommended_variant_id: variantId,
        recommendation_rank: resolved.rank,
        cart_value_before: cartValueBefore
      });

      return fetchJSON('POST', '/cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
    }).then(function (addResult) {
      if (!addResult.ok || addResult.json.status || addResult.json.description) {
        // Explicit failure path (Section J, Case 4): revert to AVAILABLE,
        // write nothing, surface a plain message, leave the recommendation
        // exactly as it was so Stage 2 can decide how to present retry.
        // Deliberately does NOT touch the existing drawer's line items or
        // subtotal -- nothing about the pre-existing cart changed.
        state = STATE_AVAILABLE;
        addBtn.disabled = false;
        select.classList.remove('is-busy'); // Stage 3.3
        statusLine.textContent = 'Could not add that size. Please try again.';
        return null;
      }

      // The add itself genuinely succeeded. V2: record this exact
      // relationship's cooldown in-memory immediately -- synchronously,
      // before any further network call, so even a same-document
      // recalculation that runs before the persistence write below
      // confirms already correctly excludes just this relationship (see
      // sessionPendingCooldowns' declaration and reconcileLedger()). Do
      // not wait for persistence, and do not let a later persistence
      // failure undo this document's own record of what just happened --
      // the customer already has the item. `state` returns to AVAILABLE
      // immediately: nothing about showing a DIFFERENT, unrelated,
      // still-eligible relationship should be blocked from here on.
      var completedAt = Date.now();
      sessionPendingCooldowns.push({ source: String(sourceProductId), target: String(resolved.productId), ts: completedAt });
      state = STATE_AVAILABLE;
      clearRoot();

      // Stage 2Q -- this module's own add goes straight to /cart/add.js via
      // fetchJSON above (see the architecture note atop this file: this
      // path deliberately does NOT go through the theme's own AJAX cart,
      // which is the only thing that ever dispatches 'cart:updated'), so
      // neither of updateBannerText()'s two normal triggers (drawerOpen.
      // CartDrawer, cart:updated) fires for THIS add on their own -- without
      // this line the banner text would stay stale (showing whatever line
      // count was true before this add) until some unrelated later
      // recalculation happened to run. The target product here is always,
      // by construction, a distinct line not already in the cart (buildCandidates()
      // filters out any candidate whose target is already present), so a
      // successful add always increases the distinct-line count by exactly
      // one -- cartLineCountBefore + 1 is therefore exact, not an estimate,
      // and needs no extra network request: cartLineCountBefore was already
      // read off the same /cart.js response cartValueBefore above came from.
      // A plain array of that length is enough since updateBannerText() only
      // ever reads cart.items.length.
      updateBannerText({ items: new Array(cartLineCountBefore + 1) });

      // Best available cart-value-after without an extra network call:
      // /cart/add.js's own response is item-shaped, not cart-shaped (it
      // does not return a fresh total_price), but it does return the
      // added line's line_price, and nothing else could have changed the
      // cart between the /cart.js read above and this response -- so
      // cartValueBefore + line_price is exact, not an estimate.
      var addedLine = addResult.json && addResult.json.items && addResult.json.items[0];
      var cartValueAfterAdd = cartValueBefore + (addedLine && typeof addedLine.line_price === 'number' ? addedLine.line_price : 0);

      // Fires here now, not after persistence -- Section E of the Stage
      // 1.5 spec requires "added" whenever the cart add itself succeeded,
      // independent of whether the completion attribute write that
      // follows succeeds or fails.
      track('tymlus_upsell_added', {
        source_product_id: sourceProductId,
        recommended_product_id: resolved.productId,
        recommended_variant_id: variantId,
        recommendation_rank: resolved.rank,
        cart_value_before: cartValueBefore,
        cart_value_after: cartValueAfterAdd
      });

      // The pre-existing, do-not-touch drawer now has stale line
      // items/subtotal (it only refreshes itself for actions taken
      // through its OWN bound controls, not for a mutation this module
      // makes independently -- verified empirically, see the comment
      // above refreshExistingDrawerDisplay). Fire-and-forget: never let
      // this delay or fail the completion flow.
      refreshExistingDrawerDisplay();

      // Now attempt to persist this relationship's cooldown at the CART
      // level so it's durable across refresh/navigation/reopen. V2: the
      // value written is the full ledger, not a flat "true" -- the prior
      // ledger (parsed from this cart's attribute as of the pre-add read,
      // so it correctly carries forward any OTHER relationship's still-
      // active cooldown already on this cart) plus this new record,
      // pruned and capped. A confirmed write is a bonus upgrade from
      // here, never a requirement for the exclusion above, which already
      // happened unconditionally via sessionPendingCooldowns.
      var priorLedger = parseLedger(attributesBefore && attributesBefore[ATTR_KEY]);
      var newRecord = { source: String(sourceProductId), target: String(resolved.productId), ts: completedAt };
      var ledgerToPersist = dedupeAndCapLedger(priorLedger.concat([newRecord]));
      var newAttrValue = serializeLedger(ledgerToPersist);

      return fetchJSON('POST', '/cart/update.js', { attributes: attrObject(newAttrValue) }).then(function (updateResult) {
        if (!updateResult.ok || !updateResult.json.attributes || updateResult.json.attributes[ATTR_KEY] !== newAttrValue) {
          // Retry once -- a single transient failure on the attribute write
          // should not silently give up on durable persistence when the
          // add itself was real.
          return fetchJSON('POST', '/cart/update.js', { attributes: attrObject(newAttrValue) });
        }
        return updateResult;
      }).then(function (finalUpdateResult) {
        var cartAfter = finalUpdateResult.json;
        if (finalUpdateResult.ok && cartAfter.attributes && cartAfter.attributes[ATTR_KEY] === newAttrValue) {
          track('tymlus_upsell_completed', {
            source_product_id: sourceProductId,
            recommended_product_id: resolved.productId,
            recommended_variant_id: variantId,
            recommendation_rank: resolved.rank,
            cart_value_before: cartValueBefore,
            cart_value_after: cartAfter.total_price
          }, 'completed:' + sourceProductId + ':' + resolved.productId); // V2 -- relationship-specific dedup key: protects against a duplicate event from THIS relationship's own retry, exactly as the old fixed 'completed' key did, while still letting a second, genuinely different relationship completed later in the same document fire its own, distinct completed event
        } else {
          // Persistence genuinely failed after the retry. This document
          // still excludes the relationship regardless (sessionPending-
          // Cooldowns already has it, unconditionally, from above) -- this
          // is NOT reverted. The durable attribute simply does not carry
          // it yet; a later page load or recalculation will resolve from
          // real cart state normally once it does.
          warnPersistenceFailed();
        }
        return finalUpdateResult;
      }).catch(function () {
        // A hard network-level failure (fetch() itself rejecting) during
        // the persistence attempt or its retry -- e.g. the connection
        // drops mid-request. Without this, such a failure would fall
        // through to the outer catch below, whose STATE_ADDING guard
        // correctly leaves state untouched but reports nothing -- silently
        // failing the "reports the persistence failure cleanly"
        // requirement for this one failure shape. Normalize it to the
        // same clean, non-reverting outcome.
        warnPersistenceFailed();
      });
    }).catch(function () {
      // Reached for a genuine network-level failure (fetch() itself
      // rejecting) anywhere in this chain -- including, potentially, the
      // /cart/update.js persistence step after a successful add. Only
      // revert to AVAILABLE-with-nothing-recorded if the add itself never
      // succeeded (state is still ADDING): once state has already moved
      // back to AVAILABLE after a successful add (see above), the real
      // cart add already happened and sessionPendingCooldowns already has
      // this relationship recorded regardless of how the persistence step
      // subsequently fails, including by throwing here.
      if (state !== STATE_ADDING) return;
      state = STATE_AVAILABLE;
      addBtn.disabled = false;
      select.classList.remove('is-busy'); // Stage 3.3
      statusLine.textContent = 'Could not add that size. Please try again.';
    });
  }

  function attrObject(value) {
    var o = {};
    o[ATTR_KEY] = value;
    return o;
  }

  /* ------------------------------------------------------------------
     Stage 2P -- info-banner singular/plural micro-copy.

     "Many of our clients also purchased this piece with the item in
     your bag." is grammatically correct for exactly one cart LINE (one
     distinct product/variant context, any quantity) and wrong for two
     or more -- "item" needs to become "items". Driven by
     cart.items.length specifically (the number of distinct cart lines
     the real Shopify Cart API returns, one entry per line), never by
     summed quantity: a single line with quantity 3 is still one line
     (cart.items.length === 1), so it stays singular, exactly as
     required; only a second distinct line flips it to plural. This is
     the same array this file already reads at the very next line below
     (`if (!cart.items.length)`) and everywhere else in this file that
     already reasons about cart lines (e.g. resolveCandidate() below),
     so no new data source, endpoint, or cart-shape assumption is
     introduced -- this uses the exact array already being read on every
     one of this file's two existing recalculation paths.

     Called unconditionally as the very first statement in recalculate()
     below, before any state/early-return branch (STATE_ADDING,
     COMPLETED/CONVERTED_PENDING suppression, empty-cart clearRoot()) --
     deliberately NOT gated behind any of them. The banner text has no
     bearing on, and no dependency on, the merchandising/candidate-
     resolution engine's own state; gating it behind that engine's early
     returns would leave the copy stale (e.g. frozen mid-STATE_ADDING)
     for a window where the real cart line count has already changed.
     Decoupling it here means it always reflects the cart exactly as of
     the most recent recalculate() call, with zero new event listeners:
     recalculate() is already the single chokepoint both of this file's
     existing cart-change listeners (drawerOpen.CartDrawer's own fresh
     GET /cart.js, and cart:updated's e.detail.cart) funnel into, so
     reusing it here covers every add/remove/quantity-change/upsell-add/
     cart-refresh path this file already reacts to, with no additional
     network request of any kind -- this reads only the `cart` object
     already being passed in.

     All ten pre-rendered candidate cards (blocks/... only one of which
     is ever unhidden at a time, per Stage 2B's pre-render-every-
     candidate architecture) share this exact same, product-agnostic
     banner sentence, so every one of their `.tymlus-upsell__banner-text`
     nodes is updated together in one pass -- never just the currently-
     visible card's -- so that whichever candidate renderCandidate() (or
     a future resolveCandidate() re-resolution) shows next already has
     the correct text the instant it becomes visible, with no separate
     per-candidate copy to keep in sync and no risk of a stale sentence
     flashing on a candidate switch. Plain .textContent assignment only
     -- no innerHTML, no new elements, no class/attribute/style change --
     so this cannot touch this element's typography, colour, icon,
     spacing, border-radius, placement or animation, all of which remain
     entirely defined by the existing, untouched CSS in
     snippets/cart-upsell.liquid. */
  var TYMLUS_BANNER_TEXT_SINGULAR = 'Many of our clients also purchased this piece with the item in your bag.';
  var TYMLUS_BANNER_TEXT_PLURAL = 'Many of our clients also purchased this piece with the items in your bag.';

  function updateBannerText(cart) {
    var lineCount = cart && cart.items ? cart.items.length : 0;
    var text = lineCount >= 2 ? TYMLUS_BANNER_TEXT_PLURAL : TYMLUS_BANNER_TEXT_SINGULAR;
    var nodes = root.querySelectorAll('.tymlus-upsell__banner-text');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== text) nodes[i].textContent = text;
    }
  }

  /* ------------------------------------------------------------------
     Recalculation entry point (Section I) -- V2.

     No longer aborts the whole module on a global completion verdict.
     reconcileLedger() only populates activeLedger (the set of currently-
     cooling-down relationships); the actual exclusion happens per-
     candidate, inside buildCandidates(), architecturally beside the
     already-in-cart filter it was modelled on. A relationship in cooldown
     therefore simply does not appear as a candidate at all -- the ordered
     fallback below still runs exactly as before and may resolve to a
     different, unrelated, still-eligible relationship.
  ------------------------------------------------------------------ */
  function recalculate(cart) {
    updateBannerText(cart); // Stage 2P -- unconditional, runs before every early return below

    if (state === STATE_ADDING) return; // frozen -- never recalculate mid-request

    reconcileLedger(cart);

    if (!cart.items.length) { clearRoot(); return; } // spec Tier D -- nothing in cart, no antecedent can qualify

    var myToken = ++recalcToken;
    resolveCandidate(cart).then(function (resolved) {
      if (myToken !== recalcToken) return; // a newer recalculation has since superseded this one -- discard to avoid a flicker/race
      if (!resolved) { clearRoot(); return; }
      // Skip a redundant re-render if the same candidate from the same
      // source is already showing -- avoids any flicker on a recalculation
      // that resolves to an identical result (Section L).
      if (currentCandidate && currentCandidate.productId === resolved.productId && currentSourceId === resolved.sourceId && !root.hidden) return;
      renderCandidate(resolved, resolved.sourceId);
    });
  }

  /* ------------------------------------------------------------------
     Stage 2M -- carousel self-recovery from an interrupted Flickity
     animation (real production defect, proven live against this exact
     deployed Theme 8.5 carousel, not theoretical).

     Root cause, traced directly in vendor-scripts-v11.js (Flickity),
     confirmed by direct reproduction against the live instance rather
     than inferred from reading the source alone:
       startAnimation(){this.isAnimating||(this.isAnimating=!0,...,this.animate())}
       animate(){...this.integratePhysics(),this.positionSlider(),this.settle(t),
         this.isAnimating&&requestAnimationFrame(()=>this.animate())}
     Every animated transition (a real swipe release, or next()/previous()/
     an animated select()) runs exactly ONE synchronous physics step
     immediately, then depends entirely on requestAnimationFrame to run
     every subsequent step -- including the step that would eventually
     call settle(), which is the ONLY place isAnimating is ever reset back
     to false. If that rAF callback never arrives -- confirmed live to
     happen deterministically whenever the animation begins while the page
     is not visible (a backgrounded/suspended Safari tab, a tab switch
     mid-swipe, a bfcache-frozen page) -- three things happen together and
     persist indefinitely, not just for one missed frame:
       1. The rendered transform freezes at whatever fraction of the
          transition the first synchronous step reached (confirmed live:
          a swipe release stopped at translate3d(-14%,...) forever, never
          reaching -100%, i.e. the reported "frozen on one image").
       2. selectedIndex (and this file's own progress-fill, which listens
          to Flickity's authoritative onChange) keep advancing completely
          normally on every subsequent swipe -- the freeze is invisible to
          this file's own state, which is exactly why it was never caught
          by the Stage 2L wrap-boundary testing (that testing exercised
          index/progress correctness, never an interrupted-animation
          visibility scenario).
       3. isAnimating stays latched true, which means startAnimation()'s
          own re-entry guard (this.isAnimating||...) silently no-ops every
          later next()/previous()/animated select() too -- so the freeze
          does not self-heal on a later swipe attempt; it is permanent
          until something outside Flickity's own animation path forces a
          resync. Confirmed live: calling positionSliderAtSelected() alone
          (the exact instant-select mechanism resetCardEntry()/
          renderCandidate() already use elsewhere in this file) repaints
          the correct position immediately, since that path is synchronous
          and never depends on rAF -- but isAnimating must also be cleared
          explicitly, or the very next real swipe attempt silently no-ops
          again for the same startAnimation() re-entry-guard reason.
     This is why every prior stage's fix (2D through 2L) never caught it:
     none of them ever interrupted an in-flight animation with the page
     hidden. It is also why it only ever appeared after "prolonged, real"
     interaction rather than a fresh load -- it requires a swipe (or a
     candidate reveal's own resize()+select() pair -- see below) to
     actually be mid-animation at the exact moment the page stops being
     visible, not merely that the page was backgrounded at some point.

     This file already runs an animated candidate-reveal transition of its
     own: renderCandidate()'s resize()+select(0,false,true) pair uses the
     INSTANT form deliberately (see the Stage 2G/Stage 2L comments above
     it), so a fresh reveal is not itself at risk -- the exposure is
     scoped entirely to a shopper's own swipe gesture (or this file's own
     onChange-driven progress read, which never issues a select() itself)
     landing mid-animation across a visibility interruption on whichever
     candidate is currently showing.

     The fix does not invent a new state machine, a timer, or a guess at
     when a resync is "needed" -- flkResync() below is unconditional and
     confirmed side-effect-free to call even when nothing is actually
     stuck (an instant reposition to the slide Flickity itself already
     considers selected is a no-op when the transform already matches),
     so no detection logic is needed. It is wired to every point in this
     file's own existing lifecycle where the page could plausibly have
     just become interactive again after a period it might not have been:
     the existing drawerOpen.CartDrawer listener (extended, run
     synchronously, independent of that listener's own network round
     trip -- recalculate()'s own de-dup check at Section L already skips
     resize()/select() entirely for an unchanged candidate, which is
     exactly the common case a resync needs to run in), plus two listeners
     new to this file: visibilitychange (only on the transition back to
     visible) and pageshow (covers a bfcache restore, where this file's
     own top-level code never re-runs at all, so nothing else could ever
     fix this). Scoped to whichever single card is actually shown
     (currentCardEntry) -- the only card a real swipe gesture could ever
     have been mid-animation on in the first place.
  ------------------------------------------------------------------ */
  function flkResync() {
    if (!currentCardEntry || !currentCardEntry.slideshow || root.hidden) return;
    var flk = currentCardEntry.slideshow.slideshow;
    // Clear the latched animation state directly -- confirmed live as
    // necessary in addition to the instant select() below: Flickity's own
    // settle() (the only other place that ever clears isAnimating) never
    // runs once its rAF chain has stopped arriving, and startAnimation()'s
    // own re-entry guard otherwise silently no-ops every later swipe.
    // velocity=0 matches what Flickity's own positionSliderAtSelected()
    // and settle() already set it to on every normal, non-interrupted
    // stop, so a real subsequent drag/swipe starts from the same clean
    // physics state a normal settle would have left it in.
    flk.isAnimating = false;
    flk.velocity = 0;
    currentCardEntry.slideshow.resize();
    flk.select(flk.selectedIndex, false, true);
  }

  /* ------------------------------------------------------------------
     Wiring into the theme's existing cart lifecycle (Section I) --
     drawerOpen.CartDrawer and cart:updated remain exactly the two
     recalculation listeners, attached exactly once, no polling, no
     setInterval, no MutationObserver. visibilitychange and pageshow
     (Stage 2M) exist purely for the carousel self-recovery above -- they
     never call recalculate() and never touch merchandising/candidate
     state.
  ------------------------------------------------------------------ */
  document.addEventListener('drawerOpen.CartDrawer', function () {
    flkResync();
    fetchJSON('GET', '/cart.js').then(function (r) { recalculate(r.json); });
  });

  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) recalculate(e.detail.cart);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') flkResync();
  });

  window.addEventListener('pageshow', function () {
    flkResync();
  });
})();
